import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";

function turkishFold(s = "") {
  const map = { İ: "i", I: "i", ı: "i", Ğ: "g", ğ: "g", Ü: "u", ü: "u", Ş: "s", ş: "s", Ö: "o", ö: "o", Ç: "c", ç: "c" };
  return s.replace(/[İIıĞğÜüŞşÖöÇç]/g, (c) => map[c] || c).toLowerCase();
}

export function SearchableSelect({ value, onChange, options, placeholder = "Seçin", disabled, testid }) {
  const [open, setOpen] = useState(false);
  const items = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const current = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between h-10 font-normal"
        >
          <span className={current ? "" : "text-muted-foreground"}>{current?.label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={(v, s) => (turkishFold(v).includes(turkishFold(s)) ? 1 : 0)}>
          <CommandInput placeholder="Ara..." />
          <CommandList>
            <CommandEmpty>Sonuç yok.</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem
                  key={i.value}
                  value={i.label}
                  onSelect={() => { onChange(i.value); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === i.value ? "opacity-100" : "opacity-0"}`} />
                  {i.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function SearchableMultiSelect({ values = [], onChange, options, placeholder = "Seçin", disabled, testid }) {
  const [open, setOpen] = useState(false);
  const items = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const toggle = (v) => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };
  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid={testid}
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between h-10 font-normal"
          >
            <span className="text-muted-foreground">{placeholder}{values.length ? ` (${values.length} seçili)` : ""}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command filter={(v, s) => (turkishFold(v).includes(turkishFold(s)) ? 1 : 0)}>
            <CommandInput placeholder="Ara..." />
            <CommandList>
              <CommandEmpty>Sonuç yok.</CommandEmpty>
              <CommandGroup>
                {items.map((i) => (
                  <CommandItem key={i.value} value={i.label} onSelect={() => toggle(i.value)}>
                    <Check className={`mr-2 h-4 w-4 ${values.includes(i.value) ? "opacity-100" : "opacity-0"}`} />
                    {i.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v) => {
            const label = items.find((i) => i.value === v)?.label || v;
            return (
              <Badge key={v} variant="secondary" className="pl-2 pr-1 gap-1">
                {label}
                <button type="button" onClick={() => toggle(v)} className="hover:bg-secondary/60 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
